import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { MetaTags } from 'react-meta-tags';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { SITE_NAME, LOCATIONS } from 'constants/index';
import { actionGetUnreadChatRoomsCount } from 'store/chat/actions';
import { connectSocket, socketEmit } from 'utils/socket';

const Meta = (props) => {
  const { name } = props;

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const unreadChatRoomsCount = useSelector((state) => state.Chat.unreadChatRoomsCount);
  const unreadChatRoomsPlaceId = useSelector((state) => state.Chat.unreadChatRoomsPlaceId);
  const chatRoomDetailId = useSelector((state) => state.Chat.chatRoomDetail?._id || '');

  const placeId = React.useMemo(
    () => new URLSearchParams(location.search).get('place') || '',
    [location.search],
  );

  const roomIdFromUrl = React.useMemo(
    () => new URLSearchParams(location.search).get('room') || '',
    [location.search],
  );

  const activeRoomId = roomIdFromUrl || chatRoomDetailId;
  const isMessagesPage = location.pathname === LOCATIONS.LOCATION_DETAIL.MESSAGES_WITH_CUSTOMERS;

  React.useEffect(() => {
    if (!placeId) {
      return;
    }

    if (unreadChatRoomsPlaceId === placeId) {
      return;
    }

    dispatch(actionGetUnreadChatRoomsCount({ place: placeId }));
  }, [dispatch, placeId, unreadChatRoomsPlaceId]);

  React.useEffect(() => {
    if (!placeId) {
      return undefined;
    }

    const socket = connectSocket();

    const joinPlaceChannel = () => {
      socketEmit('joinChannel', `place:${placeId}`);
    };

    const refreshUnreadCount = (chatRoom) => {
      if (
        isMessagesPage
        && chatRoom?._id
        && activeRoomId === chatRoom._id
      ) {
        return;
      }

      dispatch(actionGetUnreadChatRoomsCount({ place: placeId }));
    };

    socket.on('connect', joinPlaceChannel);
    socket.on('new_chat_room', refreshUnreadCount);
    socket.on('list_room_new_message', refreshUnreadCount);
    socket.on('update_chat_room', refreshUnreadCount);

    if (socket.connected) {
      joinPlaceChannel();
    }

    return () => {
      socket.off('connect', joinPlaceChannel);
      socket.off('new_chat_room', refreshUnreadCount);
      socket.off('list_room_new_message', refreshUnreadCount);
      socket.off('update_chat_room', refreshUnreadCount);
    };
  }, [dispatch, placeId, isMessagesPage, activeRoomId]);

  const metaTitle = unreadChatRoomsCount > 0
    ? `(${unreadChatRoomsCount}) ${t(name)}`
    : t(name);

  return (
    <MetaTags>
      <meta charSet="utf-8" />

      <title>
        {`${metaTitle} | ${SITE_NAME}`}
      </title>
    </MetaTags>
  );
};

Meta.propTypes = {
  name: PropTypes.string,
};

Meta.defaultProps = {
  name: '',
};

export default memo(Meta);
